import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { jsPDF } from 'jspdf';
import './PdfDownload.css';
import companyLogo from '../../assets/images/Ghoomoworld-black-logo-1.png';

// ============================================================================
// PdfDownloadContent Component
// ============================================================================
export function PdfDownloadContent({ packageItemInfo, userContact }) {
	const [imageLoaded, setImageLoaded] = useState(false);
	const [imageError, setImageError] = useState(false);
	const [base64Image, setBase64Image] = useState(null);

	const coverImage = packageItemInfo?.imageList?.[0]?.coverImage;
	const packageUrl = packageItemInfo?.packageUID
		? `https://www.divandsection.com/ghoomo-application/#/package-details/${packageItemInfo.packageUID}`
		: null;

	useEffect(() => {
		if (!coverImage) return;

		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.src = coverImage;

		img.onload = () => {
			try {
				const canvas = document.createElement('canvas');
				canvas.width = img.width;
				canvas.height = img.height;
				const ctx = canvas.getContext('2d');
				ctx.drawImage(img, 0, 0);
				const base64 = canvas.toDataURL('image/png');
				setBase64Image(base64);
				setImageLoaded(true);
			} catch (error) {
				console.error('Canvas conversion failed:', error);
				setImageError(true);
			}
		};

		img.onerror = () => {
			console.error('Failed to load image from', coverImage);
			setImageError(true);
		};
	}, [coverImage]);

	if (!packageItemInfo) return null;

	// Helper function to format day name to first 3 letters in uppercase
	const formatDayName = (dayName) => {
		if (!dayName) return '';
		return dayName.substring(0, 3).toUpperCase();
	};

	// Helper function to organize dates by month
	const organizeDatesByMonth = (dateDetails, isJoiningLeaving = false) => {
		if (!dateDetails || !Array.isArray(dateDetails)) return {};

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const datesByMonth = {};
		const seenDates = new Set();

		dateDetails.forEach((dateInfo) => {
			if (!dateInfo.fullDate) return;

			// Skip if status is not active (if status field exists)
			if (dateInfo.packageDateDetailsStatus && dateInfo.packageDateDetailsStatus !== "1") return;

			// Skip if already processed
			if (seenDates.has(dateInfo.fullDate)) return;
			seenDates.add(dateInfo.fullDate);

			// Parse date (format: DD/MM/YYYY)
			const [day, month, year] = dateInfo.fullDate.split('/');
			// Create date in local timezone to avoid timezone issues
			const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
			dateObj.setHours(0, 0, 0, 0);

			// Skip past dates (only show current and upcoming, not yesterday)
			if (dateObj < today) return;

			const monthKey = dateObj.toLocaleString('default', { month: 'short', year: 'numeric' });

			if (!datesByMonth[monthKey]) {
				datesByMonth[monthKey] = [];
			}

			// Get price from roomDetails
			let price = '';
			if (packageItemInfo?.roomDetails && packageItemInfo.roomDetails.length > 0) {
				const basePrice = isJoiningLeaving
					? packageItemInfo.roomDetails[0].packageCostForJoinLeave
					: packageItemInfo.roomDetails[0].roomPrice;
				if (basePrice) {
					price = `₹${Math.round(basePrice / 1000)}K`;
				}
			}

			datesByMonth[monthKey].push({
				date: dateObj,
				day: dateInfo.packageDate || parseInt(day),
				packageDay: dateInfo.packageDay, // Day name like Monday, Tuesday, etc.
				fullDate: dateInfo.fullDate,
				price: price,
			});
		});

		// Sort dates within each month
		Object.keys(datesByMonth).forEach((monthKey) => {
			datesByMonth[monthKey].sort((a, b) => a.date - b.date);
		});

		return datesByMonth;
	};

	// Get departure cities (excluding Joining/Leaving)
	const departureCities = packageItemInfo?.departureDetails
		? packageItemInfo.departureDetails.filter((dep) => dep.cityName !== 'Joining/Leaving')
		: [];

	// Get Joining/Leaving data
	const joiningLeavingData = packageItemInfo?.departureDetails
		? packageItemInfo.departureDetails.find((dep) => dep.cityName === 'Joining/Leaving')
		: null;

	// Organize dates for all departure cities
	const departureDatesMap = {};
	departureCities.forEach((city) => {
		if (city.packageDateDetails) {
			departureDatesMap[city.cityName] = organizeDatesByMonth(city.packageDateDetails, false);
		}
	});

	// Organize dates for Joining/Leaving
	const joiningLeavingDates = joiningLeavingData
		? organizeDatesByMonth(joiningLeavingData.packageDateDetails, true)
		: {};

	// Get all unique months in chronological order
	const getAllMonths = () => {
		const monthSet = new Set();
		const monthDateMap = new Map(); // Store month string -> first date in that month
		
		// Collect months from departure cities
		Object.values(departureDatesMap).forEach((dates) => {
			Object.keys(dates).forEach((month) => {
				monthSet.add(month);
				if (!monthDateMap.has(month) && dates[month] && dates[month].length > 0) {
					monthDateMap.set(month, dates[month][0].date);
				}
			});
		});
		
		// Collect months from joining/leaving
		Object.keys(joiningLeavingDates).forEach((month) => {
			monthSet.add(month);
			if (!monthDateMap.has(month) && joiningLeavingDates[month] && joiningLeavingDates[month].length > 0) {
				monthDateMap.set(month, joiningLeavingDates[month][0].date);
			}
		});
		
		// Sort by the actual date of the first day in each month
		return Array.from(monthSet).sort((a, b) => {
			const dateA = monthDateMap.get(a) || new Date(a);
			const dateB = monthDateMap.get(b) || new Date(b);
			return dateA - dateB;
		});
	};

	const allMonths = getAllMonths();

	return (
		<div className="pdfd-container">
			{/* HEADER */}
			<div className="pdfd-header">
				<div className="pdfd-title">{packageItemInfo?.packageName}</div>
				<img className="pdfd-logo" src={companyLogo} alt="Company Logo" />
			</div>

			{/* COVER IMAGE */}
			{coverImage && (
				<div className="pdfd-cover">
					{packageUrl ? (
						<a
							href={packageUrl}
							target="_blank"
							rel="noopener noreferrer"
							style={{ display: 'block', textDecoration: 'none' }}
						>
							{!imageLoaded && !imageError && (
								<div
									style={{
										width: '100%',
										height: '700px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										backgroundColor: '#f3f4f6',
										borderRadius: '10px',
										cursor: 'pointer',
									}}
								>
									<div>Loading image...</div>
								</div>
							)}

							{imageError && (
								<div
									style={{
										width: '100%',
										height: '880px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										backgroundColor: '#fee2e2',
										borderRadius: '10px',
										color: '#dc2626',
										cursor: 'pointer',
									}}
								>
									<div>⚠️ Image failed to load</div>
								</div>
							)}

							{base64Image && (
								<img
									src={base64Image}
									alt="Cover"
									style={{
										display: 'block',
										width: '100%',
										borderRadius: '10px',
										cursor: 'pointer',
										transition: 'transform 0.2s ease',
									}}
									onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
									onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
								/>
							)}
						</a>
					) : (
						<>
							{!imageLoaded && !imageError && (
								<div
									style={{
										width: '100%',
										height: '880px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										backgroundColor: '#f3f4f6',
										borderRadius: '10px',
									}}
								>
									<div>Loading image...</div>
								</div>
							)}

							{imageError && (
								<div
									style={{
										width: '100%',
										height: '700px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										backgroundColor: '#fee2e2',
										borderRadius: '10px',
										color: '#dc2626',
									}}
								>
									<div>⚠️ Image failed to load</div>
								</div>
							)}

							{base64Image && (
								<img
									src={base64Image}
									alt="Cover"
									style={{
										display: 'block',
										width: '100%',
										borderRadius: '10px',
									}}
								/>
							)}
						</>
					)}
				</div>
			)}

			{/* META */}
			<div className="pdfd-meta">
				<div>
					{packageItemInfo?.packageDays}D/{packageItemInfo?.packageNight}N |{' '}
					{packageItemInfo?.routeName}
				</div>
			</div>

			{/* DATES & DEPARTURE CITIES */}
			{(departureCities.length > 0 || joiningLeavingData) && (
				<div className="pdfd-section">
					<div className="pdfd-section-title">More Dates & Departure Cities</div>
					
					{/* Departure Cities Sections */}
					{departureCities.map((city) => {
						const cityDates = departureDatesMap[city.cityName] || {};
						// Get months for this specific city and sort them
						const cityMonths = Object.keys(cityDates).sort((a, b) => {
							const dateA = cityDates[a] && cityDates[a].length > 0 ? cityDates[a][0].date : new Date(a);
							const dateB = cityDates[b] && cityDates[b].length > 0 ? cityDates[b][0].date : new Date(b);
							return dateA - dateB;
						});
						
						return (
							<div key={city.cityName} className="pdfd-dates-section">
								<div className="pdfd-departure-title">
									Departure: {city.cityName}
								</div>
								<div className="pdfd-dates-container">
									{cityMonths.length > 0 ? (
										cityMonths.map((month) => {
											const dates = cityDates[month] || [];
											if (dates.length === 0) return null;
											return (
												<div key={`${city.cityName}-${month}`} className="pdfd-month-group">
													<div className="pdfd-month-label">{month}</div>
													<div className="pdfd-dates-grid">
														{dates.map((dateInfo, idx) => (
															<div key={`${city.cityName}-${month}-${idx}`} className="pdfd-date-block">
																<div className="pdfd-date-day">{formatDayName(dateInfo.packageDay)}</div>
																<div className="pdfd-date-full">{dateInfo.fullDate}</div>
															</div>
														))}
													</div>
												</div>
											);
										})
									) : (
										<div className="pdfd-empty">No dates available</div>
									)}
								</div>
							</div>
						);
					})}

					{/* Joining/Leaving Section */}
					{joiningLeavingData && (
						<div className="pdfd-dates-section">
							<div className="pdfd-departure-title">
								Departure: Joining/Leaving
							</div>
							<div className="pdfd-dates-container">
								{(() => {
									// Get months for joining/leaving and sort them
									const joinMonths = Object.keys(joiningLeavingDates).sort((a, b) => {
										const dateA = joiningLeavingDates[a] && joiningLeavingDates[a].length > 0 ? joiningLeavingDates[a][0].date : new Date(a);
										const dateB = joiningLeavingDates[b] && joiningLeavingDates[b].length > 0 ? joiningLeavingDates[b][0].date : new Date(b);
										return dateA - dateB;
									});
									
									return joinMonths.length > 0 ? (
										joinMonths.map((month) => {
											const dates = joiningLeavingDates[month] || [];
											if (dates.length === 0) return null;
											return (
												<div key={`join-${month}`} className="pdfd-month-group">
													<div className="pdfd-month-label">{month}</div>
													<div className="pdfd-dates-grid">
														{dates.map((dateInfo, idx) => (
															<div key={`join-${month}-${idx}`} className="pdfd-date-block">
																<div className="pdfd-date-day">{formatDayName(dateInfo.packageDay)}</div>
																<div className="pdfd-date-full">{dateInfo.fullDate}</div>
															</div>
														))}
													</div>
												</div>
											);
										})
									) : (
										<div className="pdfd-empty">No dates available</div>
									);
								})()}
							</div>
						</div>
					)}

					{/* Other Available Departure Cities */}
					{(departureCities.length > 1 || joiningLeavingData) && (
						<div className="pdfd-other-cities">
							<div className="pdfd-other-cities-title">Other available Departure City</div>
							<div className="pdfd-cities-list">
								{departureCities.length > 0 && (
									<div className="pdfd-city-item">
										Departure: {departureCities.map((city) => city.cityName).join(', ')}
									</div>
								)}
								{joiningLeavingData && (
									<div className="pdfd-city-item">
										Joining/Leaving: {packageItemInfo?.routeName}
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			)}

			{/* TRIP HIGHLIGHTS */}
			<div className="pdfd-section">
				<div className="pdfd-section-title">Trip Highlights</div>
				{packageItemInfo?.tripHighlights?.length ? (
					<ul className="pdfd-list">
						{packageItemInfo.tripHighlights.map((highlight, index) => (
							<li className="pdfd-list-item" key={highlight.id || `highlight-${index}`}>
								{highlight.tripHighlightsContent || highlight.content || highlight.name}
							</li>
						))}
					</ul>
				) : (
					<div className="pdfd-empty">No trip highlights provided.</div>
				)}
			</div>

			{/* ITINERARY */}
			<div className="pdfd-section">
				<div className="pdfd-section-title">Itinerary</div>
				{packageItemInfo?.itineraryList?.length ? (
					<div>
						{packageItemInfo.itineraryList.map((item) => (
							<div className="pdfd-card" key={item.itineraryId}>
								{item.dayValue && (
									<div className="pdfd-day-label">Day - {item.dayValue}</div>
								)}
								<div className="pdfd-card-title">{item.itineraryName}</div>
								<ul className="pdfd-list pdfd-list--nested">
									{item.itineraryInfoDetails?.map((info) => (
										<li className="pdfd-list-item" key={info.itineraryInfoId}>
											{info.description}
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
				) : (
					<div className="pdfd-empty">No itinerary available.</div>
				)}
			</div>

			{/* INCLUSIONS */}
			<div className="pdfd-section">
				<div className="pdfd-section-title">Inclusions</div>
				{packageItemInfo?.inclusionsList?.length ? (
					<ul className="pdfd-list">
						{packageItemInfo.inclusionsList.map((inc) => (
							<li className="pdfd-list-item" key={inc.inclusionsId}>
								{inc.inclusionsName}
							</li>
						))}
					</ul>
				) : (
					<div className="pdfd-empty">No inclusions provided.</div>
				)}
			</div>

			{/* EXCLUSIONS */}
			<div className="pdfd-section">
				<div className="pdfd-section-title">Exclusions</div>
				{packageItemInfo?.exclusionsList?.length ? (
					<ul className="pdfd-list">
						{packageItemInfo.exclusionsList.map((exc) => (
							<li className="pdfd-list-item" key={exc.exclusionsId}>
								{exc.exclusionsName}
							</li>
						))}
					</ul>
				) : null}
			</div>

			{/* DETAILED TOUR PRICE */}
			{packageItemInfo?.roomDetails?.length > 0 && (
				<div className="pdfd-section">
					<div className="pdfd-section-title">Detailed tour Price</div>
					<div className="pdfd-price-note">
						Prices & discounts are Per Person for Indian Nationals only.
					</div>
					<div className="pdfd-price-table-container">
						<table className="pdfd-price-table">
							<thead>
								<tr>
									<th className="pdfd-price-table-header">Room Type</th>
									<th className="pdfd-price-table-header">Basic Price</th>
									<th className="pdfd-price-table-header">Joining/Leaving Price</th>
								</tr>
							</thead>
							<tbody>
								{packageItemInfo.roomDetails
									.filter((room) => room.roomDetailsStatus === "1")
									.map((room) => (
										<tr key={room.roomId} className="pdfd-price-table-row">
											<td className="pdfd-price-table-cell">{room.roomType}</td>
											<td className="pdfd-price-table-cell">₹{room.roomPrice?.toLocaleString('en-IN') || '0'}</td>
											<td className="pdfd-price-table-cell">₹{room.packageCostForJoinLeave?.toLocaleString('en-IN') || '0'}</td>
										</tr>
									))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* FOOTER ON SEPARATE PAGE */}
			<div className="pdfd-footer-page">
				<div className="pdfd-footer">
					<div className="pdfd-footer-logo">
						<img className="pdfd-logo" src={companyLogo} alt="Company Logo" />
					</div>

					<div className="pdfd-footer-contact">
						Explore enchanting destinations in {packageItemInfo.routeName} with Ghoomo World's expert, caring and professional Tour Manager. Secure your spot by contacting us on our toll-free number{' '}
						<a href="tel:1800227979" className="pdfd-footer-link">1800227979</a>
						{' '}or write to us on{' '}
						<a href="mailto:travel@Ghoomoworld.com" className="pdfd-footer-link">travel@Ghoomoworld.com</a>.
					</div>

					<div className="pdfd-footer-line"></div>

					<div className="pdfd-footer-bottom">
						<div className="pdfd-footer-tagline">Travel. Explore. Celebrate Life with Ghoomo World.</div>
						<div className="pdfd-footer-copyright">©2013-24 Ghoomo World Pvt Ltd. All Rights Reserved.</div>
					</div>
				</div>
			</div>
		</div>
	);
}

// ============================================================================
// PdfDownload Route Component
// ============================================================================
export default function PdfDownload() {
	const location = useLocation();
	const packageItemInfo = location.state?.packageItemInfo;
	const userContact = location.state?.userContact;

	if (!packageItemInfo) {
		return (
			<div style={{ padding: 16 }}>
				<div>Package data not found.</div>
			</div>
		);
	}

	return (
		<PdfDownloadContent packageItemInfo={packageItemInfo} userContact={userContact} />
	);
}

// ============================================================================
// PDF Generation Functions
// ============================================================================
function loadHtml2Canvas() {
	return new Promise((resolve, reject) => {
		if (window.html2canvas) return resolve(window.html2canvas);
		const script = document.createElement('script');
		script.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
		script.async = true;
		script.onload = () => resolve(window.html2canvas);
		script.onerror = () => reject(new Error('Failed to load html2canvas'));
		document.body.appendChild(script);
	});
}

// Helper function to convert image URL to data URL (handles CORS)
async function imageToDataURL(url) {
	// First try: fetch and convert via FileReader
	try {
		const response = await fetch(url, { 
			mode: 'cors',
			credentials: 'omit'
		});
		if (!response.ok) throw new Error(`HTTP ${response.status}`);
		const blob = await response.blob();
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result);
			reader.onerror = () => {
				console.warn('FileReader failed for:', url);
				reject(new Error('FileReader error'));
			};
			reader.readAsDataURL(blob);
		});
	} catch (fetchError) {
		console.warn('Fetch failed, trying canvas method:', url, fetchError.message);
		// Fallback: try to load image in img element and convert via canvas
		return new Promise((resolve) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			
			const timeout = setTimeout(() => {
				console.warn('Image load timeout in canvas method:', url);
				resolve(url); // Return original URL as fallback
			}, 15000);
			
			img.onload = () => {
				clearTimeout(timeout);
				try {
					const canvas = document.createElement('canvas');
					canvas.width = img.naturalWidth;
					canvas.height = img.naturalHeight;
					const ctx = canvas.getContext('2d');
					ctx.drawImage(img, 0, 0);
					const dataURL = canvas.toDataURL('image/png');
					console.log('Successfully converted image via canvas:', url);
					resolve(dataURL);
				} catch (e) {
					console.warn('Canvas conversion failed:', e);
					resolve(url); // Return original URL as fallback
				}
			};
			img.onerror = () => {
				clearTimeout(timeout);
				console.warn('Image load failed in canvas fallback:', url);
				resolve(url); // Return original URL as fallback
			};
			img.src = url;
		});
	}
}

// Helper function to verify image is visible and loaded
function waitForImageVisible(img) {
	return new Promise((resolve) => {
		// Check if already loaded and visible
		if (img.complete && img.naturalHeight > 0 && img.offsetWidth > 0) {
			resolve();
			return;
		}

		const timeout = setTimeout(() => {
			console.warn('Image visibility timeout:', img.src);
			resolve(); // Resolve anyway to not block
		}, 10000);

		const checkVisible = () => {
			if (img.complete && img.naturalHeight > 0 && img.offsetWidth > 0) {
				clearTimeout(timeout);
				resolve();
			}
		};

		img.onload = checkVisible;
		img.onerror = () => {
			clearTimeout(timeout);
			console.warn('Image load error:', img.src);
			resolve(); // Resolve even on error
		};

		// Also check periodically in case onload already fired
		const interval = setInterval(() => {
			if (img.complete && img.naturalHeight > 0 && img.offsetWidth > 0) {
				clearInterval(interval);
				clearTimeout(timeout);
				resolve();
			}
		}, 100);
	});
}

// Helper function to preload and convert images to data URLs
async function preloadAndConvertImages(container) {
	const images = container.querySelectorAll('img');
	const promises = Array.from(images).map(async (img) => {
		// Skip if already a data URL
		if (img.src.startsWith('data:')) {
			await waitForImageVisible(img);
			return;
		}
		
		// Skip if image is from same origin (local assets)
		try {
			const url = new URL(img.src, window.location.href);
			if (url.origin === window.location.origin) {
				// Same origin, wait for load and visibility
				await waitForImageVisible(img);
				return;
			}
		} catch (e) {
			// Invalid URL, try to convert anyway
		}
		
		// For external images, convert to data URL first
		try {
			const dataURL = await imageToDataURL(img.src);
			img.src = dataURL;
			// Wait for the image to update and become visible
			await new Promise(resolve => setTimeout(resolve, 150));
			await waitForImageVisible(img);
		} catch (error) {
			console.warn('Failed to convert image:', img.src, error);
			// Still wait for original image to load
			await waitForImageVisible(img);
		}
	});
	
	await Promise.all(promises);
	// Additional wait to ensure all images are fully rendered in DOM
	await new Promise(resolve => setTimeout(resolve, 300));
}

export async function generatePackagePdf(packageItemInfo, userContact) {
	const container = document.createElement('div');
	container.style.position = 'fixed';
	container.style.left = '-10000px';
	container.style.top = '0';
	// A4 width at 96dpi minus margins (10mm each side = ~38px each side at 96dpi)
	// A4 width: 210mm = 794px at 96dpi, so with 10mm margins: 190mm = ~717px
	container.style.width = '717px'; /* A4 width minus margins */
	container.style.background = '#fff';
	container.style.zIndex = '-1';
	document.body.appendChild(container);

	const root = createRoot(container);
	root.render(<PdfDownloadContent packageItemInfo={packageItemInfo} userContact={userContact} />);

	// Wait for React to render
	await new Promise(resolve => setTimeout(resolve, 200));

	// Wait for all images to load and convert external images to data URLs
	const node = container.firstElementChild;
	await preloadAndConvertImages(node);

	const html2canvas = await loadHtml2Canvas();
	const scale = 2; // better quality
	const canvas = await html2canvas(node, { 
		scale, 
		useCORS: true,
		allowTaint: false, // Don't allow tainted canvas
		backgroundColor: '#ffffff',
		logging: false,
		imageTimeout: 20000,
		removeContainer: false,
		onclone: (clonedDoc) => {
			// Ensure all images in cloned document are loaded
			const clonedImages = clonedDoc.querySelectorAll('img');
			clonedImages.forEach((img) => {
				if (img.src && !img.complete) {
					img.style.display = 'block';
				}
			});
		}
	});

	const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
	const pageWidth = pdf.internal.pageSize.getWidth();
	const pageHeight = pdf.internal.pageSize.getHeight();
	const margin = 10; // 10mm margin on all sides
	const contentWidth = pageWidth - (2 * margin);
	const contentHeight = pageHeight - (2 * margin);

	// Calculate image dimensions to fit content area while keeping aspect ratio
	const imgWidth = contentWidth;
	const imgHeight = (canvas.height * contentWidth) / canvas.width;

	let position = 0;
	let remainingHeight = imgHeight;

	// Handle multi-page if needed
	while (remainingHeight > 0) {
		const sliceHeightPx = (contentHeight * canvas.width) / contentWidth; // height in px corresponding to content area
		const canvasPage = document.createElement('canvas');
		canvasPage.width = canvas.width;
		canvasPage.height = Math.min(sliceHeightPx, canvas.height - position);
		const ctx = canvasPage.getContext('2d');
		ctx.drawImage(canvas, 0, -position, canvas.width, canvas.height);
		const imgPage = canvasPage.toDataURL('image/png');
		const pageImgHeight = (canvasPage.height * contentWidth) / canvas.width;

		if (position > 0) pdf.addPage();
		// Add image with margins
		pdf.addImage(imgPage, 'PNG', margin, margin, contentWidth, pageImgHeight);

		position += canvasPage.height;
		remainingHeight -= contentHeight;
	}

	pdf.save(`${packageItemInfo?.packageName || 'Itinerary'}.pdf`);

	// cleanup
	root.unmount();
	document.body.removeChild(container);
}

// Generate PDF and return as blob (for email sending)
export async function generatePackagePdfBlob(packageItemInfo, userContact) {
	const container = document.createElement('div');
	container.style.position = 'fixed';
	container.style.left = '-10000px';
	container.style.top = '0';
	container.style.width = '717px'; /* A4 width minus margins */
	container.style.background = '#fff';
	container.style.zIndex = '-1';
	document.body.appendChild(container);

	const root = createRoot(container);
	root.render(<PdfDownloadContent packageItemInfo={packageItemInfo} userContact={userContact} />);

	// Wait for React to render
	await new Promise(resolve => setTimeout(resolve, 200));

	// Wait for all images to load and convert external images to data URLs
	const node = container.firstElementChild;
	await preloadAndConvertImages(node);

	const html2canvas = await loadHtml2Canvas();
	const scale = 2; // better quality
	const canvas = await html2canvas(node, { 
		scale, 
		useCORS: true,
		allowTaint: false,
		backgroundColor: '#ffffff',
		logging: false,
		imageTimeout: 20000,
		removeContainer: false,
		onclone: (clonedDoc) => {
			const clonedImages = clonedDoc.querySelectorAll('img');
			clonedImages.forEach((img) => {
				if (img.src && !img.complete) {
					img.style.display = 'block';
				}
			});
		}
	});

	const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
	const pageWidth = pdf.internal.pageSize.getWidth();
	const pageHeight = pdf.internal.pageSize.getHeight();
	const margin = 10; // 10mm margin on all sides
	const contentWidth = pageWidth - (2 * margin);
	const contentHeight = pageHeight - (2 * margin);

	const imgHeight = (canvas.height * contentWidth) / canvas.width;

	let position = 0;
	let remainingHeight = imgHeight;

	// Handle multi-page if needed
	while (remainingHeight > 0) {
		const sliceHeightPx = (contentHeight * canvas.width) / contentWidth;
		const canvasPage = document.createElement('canvas');
		canvasPage.width = canvas.width;
		canvasPage.height = Math.min(sliceHeightPx, canvas.height - position);
		const ctx = canvasPage.getContext('2d');
		ctx.drawImage(canvas, 0, -position, canvas.width, canvas.height);
		const imgPage = canvasPage.toDataURL('image/png');
		const pageImgHeight = (canvasPage.height * contentWidth) / canvas.width;

		if (position > 0) pdf.addPage();
		pdf.addImage(imgPage, 'PNG', margin, margin, contentWidth, pageImgHeight);

		position += canvasPage.height;
		remainingHeight -= contentHeight;
	}

	// Get PDF as blob instead of saving
	const pdfBlob = pdf.output('blob');

	// cleanup
	root.unmount();
	document.body.removeChild(container);

	return pdfBlob;
}
